<template>
  <div class="committee-report">
    <b-row class="h-100 no-gutters">
      <b-col cols="9" class="h-100">
        <b-card id="report" class="h-100" no-body>
          <div class="report-print-root" :class="printRootClass">
            <ReportPrintHeader
              v-if="printing"
              :title="printTitle"
              :subtitle="`Relatório de comitê · Total: ${totalFilteredCount}`"
              :logo="printLogo"
              :generated-at="formatPrintDateTime(printGeneratedAt)"
              :company="printCompany"
              :period="printPeriod"
              :emitter="printEmitter"
            />
            <b-card-body class="position-relative">
              <div
                class="d-flex justify-content-between align-items-center mb-3 report-screen-heading"
              >
                <h2>{{ reportTitle }}</h2>
                <b-badge variant="info" pill class="p-2">
                  <i class="fas fa-file-alt mr-1"></i>
                  Total: {{ totalFilteredCount }}
                </b-badge>
              </div>

              <div class="complaints-layout-surface">
                <div
                  v-if="!reportItems.length"
                  class="complaints-layout-empty text-center"
                >
                  <p>Nenhum item encontrado para os filtros aplicados.</p>
                </div>

                <b-table
                  v-show="reportItems.length > 0"
                  borderless
                  hover
                  :responsive="!printing"
                  :items="reportItems"
                  :fields="printFields"
                  :filter="currentFilter"
                  :filter-function="filterResults"
                  table-class="complaints-layout-table"
                  thead-class="complaints-layout-header"
                >
                  <template #cell(protocol)="data">
                    <b-button
                      v-if="!printing"
                      variant="link"
                      class="p-0 complaints-layout-link"
                      @click="goToComplaint(data.item._id)"
                    >
                      {{ data.value }}
                    </b-button>
                    <span v-else class="complaints-layout-link">
                      {{ data.value }}
                    </span>
                  </template>
                  <template #cell(hasCommittee)="data">
                    {{ data.value ? "Sim" : "Não" }}
                  </template>
                  <template #cell(hasInactiveUser)="data">
                    {{ data.value ? "Sim" : "Não" }}
                  </template>
                  <template #cell(status)="data">
                    <b-badge :variant="getStatusVariant(data.value)">
                      {{ getStatusText(data.value) }}
                    </b-badge>
                  </template>
                  <template #cell(createdAt)="data">
                    {{ formatDate(data.value) }}
                  </template>
                </b-table>
              </div>
            </b-card-body>
          </div>
        </b-card>
      </b-col>

      <b-col cols="3" class="pl-3">
        <b-card header="Parâmetros" class="filters-sidebar h-100">
          <div class="filters-container">
            <div v-if="showCompanyFilter" class="filter-group">
              <div class="filter-header" @click="toggleSection('empresa')">
                <h6 class="filter-title">Empresa</h6>
                <b-button variant="link" size="sm" class="collapse-btn p-0">
                  <i
                    :class="
                      collapsedSections.empresa
                        ? 'fas fa-chevron-down'
                        : 'fas fa-chevron-up'
                    "
                  />
                </b-button>
              </div>
              <b-collapse
                id="collapse-empresa"
                :visible="!collapsedSections.empresa"
              >
                <b-form-group label="Selecionar Empresa" class="mb-3">
                  <b-form-select
                    v-model="filter.selectedCompany"
                    :options="companyOptions"
                    @change="onFilterChange"
                  />
                </b-form-group>
              </b-collapse>
            </div>

            <div class="filter-group">
              <div class="filter-header" @click="toggleSection('data')">
                <h6 class="filter-title">Período</h6>
                <b-button variant="link" size="sm" class="collapse-btn p-0">
                  <i
                    :class="
                      collapsedSections.data
                        ? 'fas fa-chevron-down'
                        : 'fas fa-chevron-up'
                    "
                  />
                </b-button>
              </div>
              <b-collapse id="collapse-data" :visible="!collapsedSections.data">
                <b-form-group label="Data inicial" class="mb-3">
                  <b-form-input
                    v-model="filter.fromDate"
                    type="date"
                    @input="onFilterChange"
                  />
                </b-form-group>
                <b-form-group label="Data final" class="mb-3">
                  <b-form-input
                    v-model="filter.toDate"
                    type="date"
                    @input="onFilterChange"
                  />
                </b-form-group>
              </b-collapse>
            </div>

            <div class="filter-group">
              <div class="filter-header" @click="toggleSection('status')">
                <h6 class="filter-title">Situação</h6>
                <b-button variant="link" size="sm" class="collapse-btn p-0">
                  <i
                    :class="
                      collapsedSections.status
                        ? 'fas fa-chevron-down'
                        : 'fas fa-chevron-up'
                    "
                  />
                </b-button>
              </div>
              <b-collapse
                id="collapse-status"
                :visible="!collapsedSections.status"
              >
                <b-form-checkbox-group
                  v-model="filter.status"
                  :options="statusOpts"
                  stacked
                  class="mb-3"
                  @change="onFilterChange"
                />
              </b-collapse>
            </div>

            <div class="d-flex flex-column mt-3">
              <b-button variant="primary" @click="print">
                <i class="fas fa-print"></i> Imprimir
              </b-button>
              <b-button variant="success" class="mt-2" @click="exportCSV">
                <i class="fas fa-file-csv"></i> Exportar CSV
              </b-button>
            </div>
          </div>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { committeeReport } from "../../../api/reports";
import { getStatusText } from "../utils";
import ComplaintStatus from "../../../../../backend/models/ComplaintStatus.ts";
import moment from "moment";
import { getCompanies } from "@/api/company";
import ReportPrintHeader from "@/components/ReportPrintHeader.vue";
import reportPrintMixin from "@/mixins/reportPrint";

export default {
  name: "CommitteeReport",
  components: {
    ReportPrintHeader,
  },
  mixins: [reportPrintMixin],
  data() {
    const statusOpts = [];
    for (const status of Object.keys(ComplaintStatus)) {
      const text = getStatusText(ComplaintStatus[status]);
      statusOpts.push({ value: ComplaintStatus[status], text });
    }

    return {
      statusOpts,
      filter: {
        selectedCompany: null,
        fromDate: null,
        toDate: null,
        status: [],
      },
      currentFilter: {
        selectedCompany: null,
        status: [],
        toDate: null,
        fromDate: null,
      },
      report: [],
      fields: [
        { key: "protocol", label: "Protocolo", sortable: true },
        { key: "status", label: "Status", sortable: true },
        { key: "size", label: "Nº Integrantes", sortable: true },
        { key: "hasCommittee", label: "Comitê", sortable: true },
        {
          key: "hasInactiveUser",
          label: "Integrante Inativo",
          sortable: true,
        },
        { key: "createdAt", label: "Data de Criação", sortable: true },
      ],
      availableCompanies: [],
      collapsedSections: {
        empresa: false,
        data: false,
        status: false,
      },
    };
  },
  computed: {
    ...mapGetters(["profile"]),
    isMasterUser() {
      return this.profile.role === "root" || this.isCompanyManager;
    },
    isCompanyManager() {
      return (
        this.profile.role === "company-root" &&
        this.profile.managedCompanies &&
        this.profile.managedCompanies.length > 0
      );
    },
    showCompanyFilter() {
      return this.profile.role === "root" || this.isCompanyManager;
    },
    companyOptions() {
      const options = [{ value: null, text: "Todas as Empresas" }];

      if (this.availableCompanies && this.availableCompanies.length > 0) {
        this.availableCompanies.forEach((company) => {
          options.push({
            value: company._id,
            text: company.fantasyName || company.name,
          });
        });
      }

      return options;
    },
    showCompanyColumn() {
      return this.isMasterUser && !this.currentFilter.selectedCompany;
    },
    activeFields() {
      if (!this.showCompanyColumn) {
        return this.fields;
      }

      const [firstField, ...remainingFields] = this.fields;

      return [
        firstField,
        { key: "companyName", label: "Empresa", sortable: true },
        ...remainingFields,
      ];
    },
    reportItems() {
      if (this.isMasterUser) {
        if (!Array.isArray(this.report)) {
          return [];
        }

        return this.report.flatMap((item) => {
          const companyId = item.company?._id;
          const companyName =
            item.company?.fantasyName || item.company?.name || "N/A";

          return (item.complaints || []).map((complaint) => ({
            ...complaint,
            companyId,
            companyName,
          }));
        });
      }

      const companyId = this.report?.company?._id || this.profile.company;
      const companyName =
        this.report?.company?.fantasyName || this.report?.company?.name || "";

      return (this.report?.complaints || []).map((complaint) => ({
        ...complaint,
        companyId,
        companyName,
      }));
    },
    reportTitle() {
      if (!this.isMasterUser) {
        const companyName =
          this.report?.company?.fantasyName ||
          this.report?.company?.name ||
          "Empresa";
        return `Relatório de Comitê da ${companyName}`;
      }

      if (this.currentFilter.selectedCompany) {
        const selectedCompany = this.companyOptions.find(
          (company) => company.value === this.currentFilter.selectedCompany
        );

        return `Relatório de Comitê - ${
          selectedCompany ? selectedCompany.text : "Empresa Selecionada"
        }`;
      }

      return "Relatório de Comitê";
    },
    printTitle() {
      return this.reportTitle;
    },
    totalFilteredCount() {
      return this.reportItems.filter((row) =>
        this.filterResults(row, this.currentFilter)
      ).length;
    },
  },
  async created() {
    await this.loadCompanyPrintLogo(this.profile.company);
    await this.loadCompanies();

    let response;
    try {
      const companyId = this.isMasterUser ? null : this.profile.company;
      response = await committeeReport(companyId);
    } catch (error) {
      if (error.response && error.response.status === 403) {
        this.$bvToast.toast(error.response.data, {
          title: "Erro",
          toastClass: "custom-toast custom-toast-error",
          variant: "light",
          solid: true,
          autoHideDelay: 10000,
          noCloseButton: false,
        });
      } else {
        this.$bvToast.toast(
          (error.response && error.response.statusText) ||
            "Não foi possível carregar o relatório.",
          {
            title: "Erro",
            toastClass: "custom-toast custom-toast-error",
            variant: "light",
            solid: true,
            autoHideDelay: 10000,
            noCloseButton: false,
          }
        );
      }
      return;
    }

    this.report = response.data;
    this.onFilterChange();
  },
  methods: {
    getStatusText,
    getStatusVariant(status) {
      const statusMap = {
        [ComplaintStatus.NEW]: "primary",
        [ComplaintStatus.REJECTED]: "dark",
        [ComplaintStatus.IN_PROGRESS]: "warning",
        [ComplaintStatus.PENDING]: "info",
        [ComplaintStatus.DONE]: "success",
        [ComplaintStatus.CONCLUDED]: "success",
        [ComplaintStatus.REOPENED]: "warning",
        [ComplaintStatus.FORWARDED]: "primary",
      };

      return statusMap[status] || "secondary";
    },
    formatDate(value) {
      if (!value) {
        return "-";
      }

      return moment(value).format("DD/MM/YYYY HH:mm");
    },
    onFilterChange() {
      if (this.filter.fromDate) {
        const dt = new Date(this.filter.fromDate);
        const offset = dt.getTimezoneOffset();
        this.currentFilter.fromDate = moment(dt)
          .add("minute", offset)
          .startOf("day")
          .toDate();
      } else {
        this.currentFilter.fromDate = null;
      }

      if (this.filter.toDate) {
        const dt = new Date(this.filter.toDate);
        const offset = dt.getTimezoneOffset();
        this.currentFilter.toDate = moment(dt)
          .add("minute", offset)
          .endOf("day")
          .toDate();
      } else {
        this.currentFilter.toDate = null;
      }

      this.currentFilter.selectedCompany = this.filter.selectedCompany;
      this.currentFilter.status = this.filter.status;
    },
    filterResults(row, filter) {
      if (filter.selectedCompany && row.companyId !== filter.selectedCompany) {
        return false;
      }

      if (filter.fromDate || filter.toDate) {
        const created = moment(row.createdAt).toDate();

        if (filter.fromDate && filter.fromDate > created) {
          return false;
        }

        if (filter.toDate && filter.toDate < created) {
          return false;
        }
      }

      return !(
        filter.status.length && filter.status.indexOf(row.status) === -1
      );
    },
    toggleSection(section) {
      this.collapsedSections[section] = !this.collapsedSections[section];
    },
    exportCSV() {
      const escapeCsv = (value) => {
        if (value === null || value === undefined) return "";
        if (Array.isArray(value)) value = value.join(", ");
        if (typeof value === "object") value = JSON.stringify(value);

        value = String(value);
        const needsQuotes = /[",\n\r;]/.test(value);
        const escaped = value.replace(/"/g, '""');

        return needsQuotes ? `"${escaped}"` : escaped;
      };

      const formatValue = (field, row) => {
        switch (field.key) {
          case "status":
            return this.getStatusText(row.status);
          case "hasCommittee":
          case "hasInactiveUser":
            return row[field.key] ? "Sim" : "Não";
          case "createdAt":
            return this.formatDate(row.createdAt);
          default:
            return row[field.key] == null ? "" : row[field.key];
        }
      };

      const fieldsToExport = this.fields;
      const headers = fieldsToExport.map((f) => f.label || f.key);
      const includeCompany = this.showCompanyColumn;
      const headerRow = includeCompany ? ["Empresa", ...headers] : headers;

      const rows = this.reportItems
        .filter((row) => this.filterResults(row, this.currentFilter))
        .map((row) => {
          const csvRow = fieldsToExport.map((field) => formatValue(field, row));
          return includeCompany ? [row.companyName, ...csvRow] : csvRow;
        });

      const separator = ";";
      const lines = [headerRow, ...rows]
        .map((row) => row.map(escapeCsv).join(separator))
        .join("\r\n");

      const bom = "\ufeff";
      const blob = new Blob([bom + lines], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      const ts = moment().format("YYYYMMDD_HHmmss");

      anchor.href = url;
      anchor.download = `relatorio_comite_${ts}.csv`;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      URL.revokeObjectURL(url);
    },
    goToComplaint(complaintId) {
      this.$router.push({ path: `/denuncias/${complaintId}` });
    },
    async loadCompanies() {
      if (!this.profile || !this.isMasterUser) {
        return;
      }

      try {
        let companies = [];

        if (this.profile.role === "root") {
          const response = await getCompanies({ size: 1000, page: 1 });
          if (response.data && response.data.docs) {
            companies = response.data.docs;
          } else if (Array.isArray(response.data)) {
            companies = response.data;
          }
        } else if (this.profile.role === "company-root") {
          const response = await getCompanies();
          companies = Array.isArray(response.data)
            ? response.data
            : response.data.docs || [];
        }

        this.availableCompanies = companies;
      } catch (error) {
        this.$bvToast.toast("Não foi possível carregar as empresas.", {
          title: "Erro",
          toastClass: "custom-toast custom-toast-error",
          variant: "light",
          solid: true,
          autoHideDelay: 10000,
          noCloseButton: false,
        });
      }
    },
  },
};
</script>

<style scoped>
.committee-report {
  height: 100vh;
  overflow: hidden;
}

.committee-report .row {
  height: 100%;
  margin: 0;
  align-items: stretch;
}

.committee-report .col-3,
.committee-report .col-9 {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.committee-report .col-9 {
  padding-right: 15px;
}

.committee-report .col-3 {
  padding-left: 15px;
}

.committee-report #report.card {
  border: none;
  border-radius: 8px;
}

.committee-report .card-body {
  overflow-y: auto;
}

.filters-sidebar {
  height: 100%;
  display: flex;
  flex-direction: column;
  background-color: #f8f9fa;
  border: none;
  border-radius: 8px;
}

.filters-sidebar >>> .card-header {
  background-color: transparent;
  border-bottom: 1px solid #e9ecef;
  font-weight: 600;
  color: #343a40;
  font-size: 1.1rem;
}

.filters-sidebar .card-body {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.filters-container {
  flex: 1;
  overflow-y: auto;
  padding-right: 10px;
}

.filters-container::-webkit-scrollbar {
  width: 6px;
}

.filters-container::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 3px;
}

.filter-group {
  margin-bottom: 1.5rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid #e9ecef;
}

.filter-group:last-child {
  border-bottom: none;
}

.filter-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  padding: 0;
}

.filter-title {
  color: #495057;
  font-weight: 600;
  margin-bottom: 0.75rem;
  flex: 1;
  font-size: 0.9rem;
}

.collapse-btn {
  color: #6c757d;
  line-height: 1;
}

.collapse-btn:hover {
  color: #007bff;
  text-decoration: none;
}

.complaints-layout-empty {
  padding: 2rem;
}

.complaints-layout-link {
  font-weight: 600;
}

@media (max-width: 1200px) {
  .committee-report {
    height: auto;
    min-height: 100vh;
  }

  .committee-report .row {
    height: auto;
    min-height: 100vh;
  }
}

@media (max-width: 768px) {
  .committee-report .col-9,
  .committee-report .col-3 {
    flex: 0 0 100%;
    max-width: 100%;
    height: auto;
    padding-left: 0;
    padding-right: 0;
  }

  .committee-report .col-3 {
    margin-top: 1rem;
  }
}

@media print {
  .filters-sidebar,
  .committee-report .col-3 {
    display: none !important;
  }

  .committee-report .col-9 {
    max-width: 100% !important;
    flex: 0 0 100% !important;
    padding-right: 0;
  }
}
</style>
